import { Trans, useLingui } from '@lingui/react/macro';
import { Lock } from 'lucide-react';
import {
  memo,
  type Dispatch,
  type SetStateAction,
  type HTMLAttributes,
} from 'react';

import {
  MultiSelect,
  type MultiSelectOption,
} from '@/components/multiselect/MultiSelect';

export type AgentAccessSelectorProps = {
  compact?: boolean;
  availableForAllAgents: boolean;
  setAvailableForAllAgents: (value: boolean) => void;
  selectedChatIds: string[];
  setSelectedChatIds: Dispatch<SetStateAction<string[]>>;
  availableAgents: Array<{
    id: string;
    name: string;
  }>;
  currentChatId?: string;
  /**
   * Chat IDs inherited via folder hierarchy. These are displayed as
   * locked/read-only and cannot be removed at the document level.
   */
  inheritedChatIds?: string[];
} & HTMLAttributes<HTMLDivElement>;

/**
 * Reusable component for managing document access across agents
 */
export const AgentAccessSelector = memo(function AgentAccessSelector({
  compact = false,
  availableForAllAgents,
  setAvailableForAllAgents,
  selectedChatIds,
  setSelectedChatIds,
  availableAgents,
  currentChatId,
  inheritedChatIds = [],
  ...rest
}: AgentAccessSelectorProps) {
  const { t } = useLingui();

  const getAvailableAgentsOptions = (
    allChats: Array<{
      id: string;
      name: string;
    }>,
    currentChatId?: string,
  ): MultiSelectOption[] => {
    if (!allChats?.length) return [];

    let currentOption: MultiSelectOption | null = null;
    const options: MultiSelectOption[] = [];

    for (const chat of allChats) {
      const option = { value: chat.id, label: chat.name };
      if (currentChatId && chat.id === currentChatId) {
        currentOption = option;
      } else {
        options.push(option);
      }
    }

    if (currentOption) {
      options.unshift(currentOption);
    }

    return options;
  };

  return (
    <div {...rest}>
      <div className={compact ? 'mb-1 text-xs text-muted-fg' : 'mb-2 text-sm'}>
        {compact ? (
          <Trans>Agent access</Trans>
        ) : (
          <Trans>Agents with access to this document</Trans>
        )}
      </div>
      <MultiSelect
        value={availableForAllAgents ? ['__all_agents__'] : selectedChatIds}
        onChange={values => {
          if (!availableForAllAgents && values.includes('__all_agents__')) {
            setAvailableForAllAgents(true);
            setSelectedChatIds([]);

            return;
          }
          setAvailableForAllAgents(false);
          const ids = values.filter(value => value !== '__all_agents__');
          setSelectedChatIds(
            ids.length ? ids : currentChatId ? [currentChatId] : [],
          );
        }}
        options={[
          { value: '__all_agents__', label: t`All agents (including new)` },
          ...getAvailableAgentsOptions(availableAgents, currentChatId),
        ]}
        placeholder={t`Select agents`}
      />
      {inheritedChatIds.length > 0 && !availableForAllAgents && (
        <div className='mt-3 space-y-2'>
          <p className='text-xs text-muted-fg'>
            <Trans>Inherited from folder hierarchy (read-only)</Trans>
          </p>
          <div className='flex flex-wrap gap-1.5'>
            {inheritedChatIds.map(chatId => {
              const chat = availableAgents.find(c => c.id === chatId);

              return (
                <span
                  key={chatId}
                  className='inline-flex items-center gap-1 rounded-md border border-dashed border-border bg-muted/50 px-2 py-0.5 text-xs text-muted-fg'
                >
                  <Lock className='size-3' />
                  {chat?.name ?? chatId}
                </span>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
});
